import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import ShellSaac, { useVoltar } from '../../../components/shell/ShellSaac';
import {
  Botao,
  Checkbox,
  Chip,
  DataList,
  EmptyState,
  ErrorState,
  Nota,
  PageHeader,
  Skeleton,
} from '../../../ui';

import { TI, TIPLANO } from '../routes';
import {
  ListaPlanosCotacao,
  PlanoCotacaoResumo,
  TipoSemPlano,
  listarPlanosCotacao,
} from '../../../api/plano-cotacao/planoCotacaoApi';
import { rotuloTipoNoSga } from '../../../utils/planoCotacaoRegras';
import { extrairMensagemErro } from '../../../utils/apiError';

/**
 * Planos de cotação (at-43, item 6; reescrito na at-69, frente D/D7).
 *
 * O conteúdo de Coberturas, Assistências e Benefícios que sai no PDF da cotação.
 * A tela é 100% banco local — nenhuma chamada à Hinova (princípio de resiliência).
 *
 * ## O que mudou na at-69
 *
 *   - **Erro deixou de ser mudo** (era `toast` + tabela vazia dizendo "Nenhum
 *     plano cadastrado.", que o TI lê como "não há plano").
 *   - **As explicações de "não encontrado no SGA" e "inativo no SGA" viraram
 *     TEXTO** — eram `title`, invisível no celular e no teclado (at-65).
 *   - **"Mostrar inativos" virou `Checkbox`** de verdade: era um botão que trocava
 *     o próprio rótulo, e um controle de estado com dois nomes diferentes obriga a
 *     ler o texto para saber em que estado se está.
 *   - **O editor virou ficha em sub-rota** (`/ti/planos/:codigo`) — era um modal de
 *     463 linhas com abas e três seções reordenáveis. F5 deixou de perder tudo.
 *   - Fim do `useSelector(accessToken)` como guarda (redundante desde a at-17: o
 *     interceptor injeta o Bearer e trata o 401), do `FullScreenLoader` e da
 *     `<table>` que não colapsava.
 */

type Estado = 'carregando' | 'ok' | 'erro';

const PlanosCotacaoPage: React.FC = () => {
  const navigate = useNavigate();
  const voltar = useVoltar(TI());

  const [dados, setDados] = useState<ListaPlanosCotacao>({ planos: [], semPlano: [] });
  const [estado, setEstado] = useState<Estado>('carregando');
  const [erro, setErro] = useState('');
  /* O catálogo inteiro do SGA está cadastrado (os tipos não vendidos ficam como
     planos inativos, at-43), então a lista abre só com o que está em uso. */
  const [mostrarInativos, setMostrarInativos] = useState(false);

  const carregar = useCallback(async () => {
    setEstado('carregando');
    try {
      setDados(await listarPlanosCotacao());
      setEstado('ok');
    } catch (falha) {
      setDados({ planos: [], semPlano: [] });
      setErro(extrairMensagemErro(falha, 'Não foi possível carregar os planos de cotação.'));
      setEstado('erro');
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const abrirFicha = (codigo: string, descricaoTipo?: string | null) =>
    navigate(TIPLANO(codigo), { state: { descricaoTipo, planosDisponiveis: dados.planos } });

  const inativos = dados.planos.filter((p) => !p.ativo).length;
  const visiveis = mostrarInativos ? dados.planos : dados.planos.filter((p) => p.ativo);

  const colunas = [
    {
      id: 'codigo',
      rotulo: 'Código',
      secundaria: true,
      celula: (p: PlanoCotacaoResumo) => `Código ${p.codigo}`,
    },
    {
      id: 'nome',
      rotulo: 'Plano',
      principal: true,
      celula: (p: PlanoCotacaoResumo) => p.nome,
    },
    {
      id: 'tipo',
      rotulo: 'Tipo de veículo (SGA)',
      celula: (p: PlanoCotacaoResumo) => {
        const alerta = rotuloTipoNoSga(p);

        return (
          <span>
            {p.tipoVeiculoConhecido ? (p.descricaoTipoVeiculo ?? `Tipo ${p.codigo}`) : null}
            {alerta && (
              <>
                {p.tipoVeiculoConhecido && ' '}
                <Chip variante={alerta.variante}>{alerta.texto}</Chip>
                {/* A explicação era um `title` — aqui ela fica visível. */}
                <span className="tw-mt-1 tw-block tw-text-xs tw-text-texto-suave">
                  {alerta.descricao}
                </span>
              </>
            )}
          </span>
        );
      },
    },
    {
      id: 'conteudo',
      rotulo: 'Conteúdo',
      ocultarNoCard: true,
      celula: (p: PlanoCotacaoResumo) =>
        `${p.totalCoberturas} cob. · ${p.totalAssistencias} assist. · ${p.totalBeneficios} benef.`,
    },
    {
      id: 'situacao',
      rotulo: 'Situação',
      celula: (p: PlanoCotacaoResumo) => (
        <Chip variante={p.ativo ? 'ok' : 'neutro'}>{p.ativo ? 'Ativo' : 'Inativo'}</Chip>
      ),
    },
  ];

  const conteudo = () => {
    if (estado === 'carregando') return <Skeleton linhas={5} altura="tw-h-14" />;

    if (estado === 'erro') {
      return (
        <ErrorState
          mensagem="Não foi possível carregar os planos de cotação."
          detalhe={erro}
          aoTentarNovamente={() => void carregar()}
        />
      );
    }

    if (visiveis.length === 0) {
      if (dados.planos.length > 0) {
        return (
          <EmptyState
            titulo="Nenhum plano ativo"
            descricao={`Há ${inativos} plano(s) inativo(s) — tipos que a associação não vende.`}
            acao={
              <Botao variante="secundario" onClick={() => setMostrarInativos(true)}>
                Mostrar inativos
              </Botao>
            }
          />
        );
      }

      return (
        <EmptyState
          icone="sem-dados"
          titulo="Nenhum plano cadastrado"
          descricao="Os planos aparecem aqui depois que o TI cadastra o conteúdo de cada tipo de veículo."
        />
      );
    }

    return (
      <DataList
        itens={visiveis}
        colunas={colunas}
        chave={(p) => p.codigo}
        rotuloAcessivel="Planos de cotação"
        acoes={(p) => (
          <Botao
            variante="secundario"
            tamanho="sm"
            onClick={() => abrirFicha(p.codigo, p.descricaoTipoVeiculo)}
            className="tw-w-full md:tw-w-auto"
          >
            Editar
          </Botao>
        )}
      />
    );
  };

  return (
    <ShellSaac
      cabecalho={
        <PageHeader
          titulo="Planos de Cotação"
          sobretitulo="Administração (TI)"
          aoVoltar={voltar}
        />
      }
    >
      <div className="tw-min-h-full tw-bg-shell">
        <div className="tw-mx-auto tw-w-full tw-max-w-6xl md:tw-p-6">
          <div className="tw-bg-white tw-p-4 md:tw-rounded-2xl md:tw-border md:tw-border-superficie-borda md:tw-p-6 md:tw-shadow-[0_2px_12px_rgba(0,0,0,0.09)]">
            <div className="tw-flex tw-flex-col tw-gap-4">
              {/* Texto preservado da tela antiga (D8): é orientação operacional
                  aprovada, e mudá-la é outra tarefa. */}
              <Nota>
                O conteúdo de <strong>Coberturas</strong>, <strong>Assistências</strong> e{' '}
                <strong>Benefícios</strong> que aparece no PDF da cotação. Cada plano corresponde a
                um <strong>Tipo de Veículo</strong> do SGA. Alterações valem para as{' '}
                <strong>próximas</strong> cotações — as já geradas não mudam.
              </Nota>

              {dados.semPlano.length > 0 && (
                <Nota variante="atencao">
                  <strong>
                    {dados.semPlano.length} tipo(s) de veículo do SGA sem plano definido.
                  </strong>{' '}
                  Esses tipos <strong>não aparecem</strong> na tela de cotação. Se a associação não
                  vende algum deles, cadastre-o e deixe <strong>inativo</strong> — assim a decisão
                  fica registrada e ele sai deste aviso.
                  <div className="tw-mt-2.5 tw-flex tw-flex-wrap tw-gap-2">
                    {dados.semPlano.map((tipo: TipoSemPlano) => (
                      <Botao
                        key={tipo.codigo}
                        variante="secundario"
                        tamanho="sm"
                        onClick={() => abrirFicha(tipo.codigo, tipo.descricao)}
                      >
                        {`Criar plano — ${tipo.descricao || `Tipo ${tipo.codigo}`} · #${tipo.codigo}`}
                      </Botao>
                    ))}
                  </div>
                </Nota>
              )}

              {estado === 'ok' && dados.planos.length > 0 && (
                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-4 tw-gap-y-2">
                  <Checkbox
                    rotulo={`Mostrar inativos${inativos > 0 ? ` (${inativos})` : ''}`}
                    checked={mostrarInativos}
                    onChange={setMostrarInativos}
                    disabled={inativos === 0}
                    motivoDesabilitado="Todos os planos cadastrados estão ativos."
                  />
                  <span className="tw-text-xs tw-text-texto-suave">
                    {visiveis.length} de {dados.planos.length} plano(s)
                  </span>
                </div>
              )}

              {conteudo()}
            </div>
          </div>
        </div>
      </div>
    </ShellSaac>
  );
};

export default PlanosCotacaoPage;
